import { Box, Grid, Typography, IconButton, Tooltip, Paper } from '@mui/material';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import React, { FC, useMemo, useState } from 'react';
import HorizontalBarChart from './HorizontalBarChart';
import SourcePortfolioHorizontalStacked from './SourcePortfolioHorizontalStacked';
import ReportChartEnlargeDialog from './ReportChartEnlargeDialog';
import { prepareSegmentDisplay } from './SegmentWiseBobChart';

interface PortfolioMixAnalysisProps {
  segmentBob?: any;
  sourceRevenue?: any;
  onExportSegment: () => void;
  onExportSource: () => void;
}

const mixEmptyPaperSx = {
  minHeight: 280,
  p: 2.5,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  border: '1px dashed',
  borderColor: 'divider',
  bgcolor: '#fafbfc',
  borderRadius: 2,
} as const;

const PortfolioMixAnalysis: FC<PortfolioMixAnalysisProps> = ({
  segmentBob,
  sourceRevenue,
  onExportSegment,
  onExportSource,
}) => {
  const [enlarged, setEnlarged] = useState<null | 'segment' | 'source'>(null);

  const segmentHorizontal = useMemo(() => {
    if (!segmentBob?.datasets?.length) return null;
    const p = prepareSegmentDisplay(segmentBob);
    if (!p.labels.length) return null;
    return {
      ...segmentBob,
      labels: p.labels,
      datasets: p.datasets,
      infoText: segmentBob?.infoText || 'Segment contribution to total pipeline — portfolio mix (horizontal stacked).',
    };
  }, [segmentBob]);

  const hasSourceRows = useMemo(() => {
    if (!sourceRevenue?.labels?.length) {
      return false;
    }
    const labels = sourceRevenue.labels as unknown[];
    const ds = (sourceRevenue.datasets || []) as { label?: string; data?: unknown[] }[];
    const toNum = (v: unknown) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    const find = (needle: string) =>
      ds.findIndex(d => String(d?.label || '').toLowerCase().includes(needle));
    const ic = find('confirm');
    const it = find('tent');
    const ia = find('actual');
    return labels.some((_, i: number) => {
      const c = ic >= 0 ? toNum(ds[ic]?.data?.[i]) : 0;
      const t = it >= 0 ? toNum(ds[it]?.data?.[i]) : 0;
      const a = ia >= 0 ? toNum(ds[ia]?.data?.[i]) : 0;
      return c + t + a > 0;
    });
  }, [sourceRevenue]);

  const segmentTitle = segmentBob?.name || 'Segment-wise Business on Books';
  const sourceTitle = sourceRevenue?.name || 'Revenue by Source';

  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 0.5, color: 'text.primary' }}>Portfolio Mix Analysis</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        Aggregate, portfolio-wide view — horizontal stacked contribution bars for segment pipeline mix and source revenue
        composition.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          {segmentHorizontal ? (
            <HorizontalBarChart
              data={segmentHorizontal}
              name={segmentTitle}
              handleExport={onExportSegment}
              handleEnlarge={() => setEnlarged('segment')}
            />
          ) : (
            <Paper elevation={0} sx={mixEmptyPaperSx}>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: 'text.primary' }}>{segmentTitle}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.55 }}>
                {segmentBob?.datasets?.length
                  ? 'No visible pipeline for any segment in this range (all stacked values are zero).'
                  : 'No segment data loaded. Run search with your date range, or check event-type mapping.'}
              </Typography>
            </Paper>
          )}
        </Grid>
        <Grid item xs={12} lg={6}>
          {hasSourceRows ? (
            <SourcePortfolioHorizontalStacked
              data={sourceRevenue}
              handleExport={onExportSource}
              handleEnlarge={() => setEnlarged('source')}
            />
          ) : (
            <Paper elevation={0} sx={mixEmptyPaperSx}>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: 'text.primary' }}>{sourceTitle}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.55 }}>
                {sourceRevenue?.labels?.length
                  ? 'No visible revenue by source in this range (confirmed, tentative, and actualized are all zero).'
                  : 'No source revenue data loaded. Run search with your date range.'}
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      <ReportChartEnlargeDialog
        open={enlarged === 'segment'}
        onClose={() => setEnlarged(null)}
        title={segmentTitle}
        headerActions={
          <Tooltip title="Export" placement="bottom">
            <IconButton size="small" aria-label="Export" onClick={() => onExportSegment()}>
              <FileDownloadOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        }>
        {segmentHorizontal ? (
          <HorizontalBarChart
            key="segment-enlarged"
            data={segmentHorizontal}
            name={segmentTitle}
            embeddedInDialog
            showButton={false}
            handleExport={() => {}}
            handleEnlarge={() => {}}
          />
        ) : null}
      </ReportChartEnlargeDialog>

      <ReportChartEnlargeDialog
        open={enlarged === 'source'}
        onClose={() => setEnlarged(null)}
        title={sourceTitle}
        headerActions={
          <Tooltip title="Export" placement="bottom">
            <IconButton size="small" aria-label="Export" onClick={() => onExportSource()}>
              <FileDownloadOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        }>
        {hasSourceRows ? (
          <SourcePortfolioHorizontalStacked
            key="source-enlarged"
            data={sourceRevenue}
            embeddedInDialog
            showButton={false}
            handleExport={() => {}}
            handleEnlarge={() => {}}
          />
        ) : null}
      </ReportChartEnlargeDialog>
    </Box>
  );
};

export default PortfolioMixAnalysis;
